import Box from "@mui/material/Box";
import { Button, TextInput, useGetList, useGetOne } from "react-admin";
import { DateTime } from "luxon";
import { FormProvider, useForm } from "react-hook-form";
import { TheoryExam } from "../../model/autovioCalendarEvents";
import { Row } from "../../misc/Row";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { Student } from "../../providers/studentsProvider";
import { Column } from "../../misc/Column";
import { editTheoryExam } from "../../api/backoffice.api";
import { Form2Theme } from "../../misc/Form2";
import { StartingPointInput } from "../../inputs/StartingPointInput";
import { StartingPoint, startingPointsProvider } from "../../providers/startingPointsProvider";
import { matchAddresses } from "../../backoffice.utils";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import { SaveButton2 } from "../../buttons/SaveButton2";

interface EditTheoryExamFormProps {
  event: TheoryExam;
  closeEditMode: () => void;
}

interface EditTheoryExamFormValues {
  dateTime: DateTime;
  locationId: string;
  remarks: string | null;
}

interface EditTheoryExamParams {
  dateTime: DateTime;
  locationId: string;
  remarks?: string;
}

function validate(formValues: EditTheoryExamFormValues): EditTheoryExamParams {
  const { dateTime, locationId, remarks } = formValues;
  if (!dateTime || !dateTime.isValid) {
    throw new Error(`Invalid dateTime: ${dateTime}`);
  }
  if (!locationId) {
    throw new Error(`Invalid locationId: ${locationId}`);
  }
  return {
    dateTime,
    locationId,
    ...(remarks !== null ? { remarks } : {}),
  } satisfies EditTheoryExamParams;
}

export function EditTheoryExamForm({ event, closeEditMode }: EditTheoryExamFormProps) {
  const studentId = event.student.uid;
  const { data: student } = useGetOne<Student>("students", { id: studentId }, { enabled: !!studentId });
  const fallbackLocation = {
    id: "fallbackLocation",
    name: "???",
    postalAddress: {
      street: event.startLocation.street,
      postalCode: event.startLocation.postalCode,
      city: event.startLocation.city,
    },
  };
  const { isFetched: locationsFetched, data: locations = [] } = useGetList<StartingPoint>(
    "startingPoints",
    {
      sort: { field: "__apply_special_starting_point_sort_logic__", order: "ASC" },
      pagination: { page: 1, perPage: 999 },
      meta: { drivingLessonType: "theoretischePruefung", student },
    },
    {
      enabled: !!student,
      onSuccess: (data) => {
        if (formProps.getValues("locationId")) return;
        const locationId = data.data.find((startingPoint) =>
          matchAddresses(startingPoint.postalAddress, event.startLocation),
        )?.id;
        formProps.setValue("locationId", locationId ?? fallbackLocation.id);
      },
    },
  );
  const defaultValues = {
    dateTime: event.start,
    locationId: locations.find((it) => matchAddresses(it.postalAddress, event.startLocation))?.id ?? "",
    remarks: event.remarks ?? null,
  } satisfies EditTheoryExamFormValues;
  const formProps = useForm<EditTheoryExamFormValues>({ defaultValues });

  if (!student) {
    return null;
  }

  const onSubmit = async (formValues: EditTheoryExamFormValues) =>
    runSafely(
      async () => {
        let location: StartingPoint;
        const { dateTime, locationId, remarks } = validate(formValues);
        // Avoid fetching fallbackLocation from the provider as it only exists in the form.
        if (locationId === fallbackLocation.id) {
          location = fallbackLocation;
        } else {
          const result = await startingPointsProvider.getOne("ignored", { id: locationId });
          location = result.data;
        }
        const { street, postalCode, city } = location.postalAddress;
        await editTheoryExam({
          eventId: event.id,
          start: dateTime,
          location: {
            street,
            postalCode,
            city,
          },
          remarks,
        });
      },
      { onSuccess: closeEditMode },
    );

  const { watch } = formProps;
  const locationId = watch("locationId");

  return (
    <Box sx={{ minWidth: { xs: 0, sm: "450px" } }} data-testid="EditTheoryExamForm">
      <FormProvider {...formProps}>
        <Form2Theme>
          <form onSubmit={formProps.handleSubmit(onSubmit)}>
            <Column sx={{ gap: "20px" }}>
              <DateTimeInput source="dateTime" sx={{ mt: "5px" }} allowPastDates size="small" />
              {student && (
                <StartingPointInput
                  source="locationId"
                  drivingLessonType="theoretischePruefung"
                  student={student}
                  size="small"
                  fallbackStartingPoint={locationsFetched && !locationId ? fallbackLocation : undefined}
                />
              )}
              <TextInput source="remarks" label="Hinweis für Fahrschüler" multiline rows={2} />
            </Column>
            <Row sx={{ justifyContent: "space-between", mt: "40px" }}>
              <Button label="Zurück" variant="outlined" size="medium" onClick={closeEditMode}>
                <NavigateBeforeIcon />
              </Button>
              <SaveButton2 />
            </Row>
          </form>
        </Form2Theme>
      </FormProvider>
    </Box>
  );
}
